/**
 * Is the store module in use? Most accounts never sell anything, so the
 * Store section stays out of the nav (and out of the way) until this says
 * yes. The backend answers true when the operator enabled it in settings OR
 * the store plainly is in use (products/orders exist).
 *
 * Module-level cache: the nav asks on every render and the answer changes
 * ~never within a session. `setStoreEnabled` lets the Settings toggle flip
 * the nav instantly without a reload.
 */

import { useEffect, useState } from "react";
import { api } from "./api";

let cached: boolean | null = null;
let inflight: Promise<boolean> | null = null;
const listeners = new Set<(v: boolean) => void>();

function fetchEnabled(): Promise<boolean> {
  if (!inflight) {
    inflight = api
      .get<{ enabled: boolean }>("/store/status")
      .then((r) => {
        cached = r.enabled;
        return r.enabled;
      })
      .catch(() => {
        // If we can't tell, show the tab — hiding a store someone uses is
        // worse than showing an idle one.
        cached = true;
        return true;
      });
  }
  return inflight;
}

export function setStoreEnabled(v: boolean): void {
  cached = v;
  for (const fn of listeners) fn(v);
}

/** null while unknown (nav renders without Store until then). */
export function useStoreEnabled(): boolean | null {
  const [enabled, setEnabled] = useState<boolean | null>(cached);
  useEffect(() => {
    listeners.add(setEnabled);
    if (cached === null) void fetchEnabled().then(setEnabled);
    return () => {
      listeners.delete(setEnabled);
    };
  }, []);
  return enabled;
}
